import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { Countdown } from "../../components/Data/Countdown";

const DISTRIBUTION_CYCLE_MS = 7 * 86_400_000;

function clockProgress(
    nextDateIso: string,
    periodStartIso: string | null | undefined
): number {
    const now = Date.now();
    const end = new Date(nextDateIso).getTime();
    if (end <= now) return 1;
    // Without the period's start, assume the seven-day cycle the sims use.
    const start = periodStartIso
        ? new Date(periodStartIso).getTime()
        : end - DISTRIBUTION_CYCLE_MS;
    if (!(end > start)) return 1;
    return Math.max(0, Math.min(1, (now - start) / (end - start)));
}

interface YaasNextDistributionProps {
    /** ISO timestamp of the cycle whose countdown should display.
     *  Pass `null` (or omit) when no countdown should run — the
     *  label still renders, the countdown shows the placeholder, and
     *  the progress bar reads empty. */
    target?: string | null;
    /** Optional override for the bar's reference date. When the
     *  countdown's `target` is a multi-cycle projection (e.g. a
     *  below-min user whose first qualifying distribution is 12 days
     *  out), `clockProgress` against that target reads 0% for the
     *  entire current cycle. Pass the actual nearest cycle boundary
     *  as `barTarget` so the bar reflects within-cycle progress
     *  independently from what the countdown is showing. Defaults to
     *  `target`. */
    barTarget?: string | null;
    /** When `false`, the countdown collapses to "—" and the progress
     *  bar reads empty regardless of `target`. Used for non-Earning
     *  position states (Pending / Disabled) where the position
     *  isn't accruing toward the next distribution. */
    isActive?: boolean;
    /** ISO start of the period that ends at `barTarget`. The bar fills
     *  between the two. Omit for the seven-day cycle the sims and demos
     *  are built on. */
    periodStart?: string | null;
}

/**
 * "Next Distribution" row inside YaasPositionCard's distribution
 * group. Two stacked elements:
 *
 *   1. Progress bar (h-2) — fill width = elapsed-fraction of the
 *      current distribution period, ember accent. Empty when not active.
 *   2. Label/value row — left-aligned "Next Distribution" label,
 *      right-aligned countdown ("12d 5h 30m" via the shared
 *      `Countdown` primitive, or "—" when not active / no target).
 *
 * For below-minimum-balance positions the parent renders a different
 * explainer in this slot (no "Next Distribution" row at all).
 */
export function YaasNextDistribution({
    target = null,
    barTarget,
    isActive = true,
    periodStart
}: YaasNextDistributionProps): React.ReactElement {
    const effectiveTarget = isActive ? (target ?? null) : null;
    const effectiveBarTarget = barTarget !== undefined ? barTarget : target;
    const progress =
        isActive && effectiveBarTarget
            ? clockProgress(effectiveBarTarget, periodStart)
            : 0;
    return (
        <>
            <div className="flex items-baseline justify-between gap-3">
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-muted"
                >
                    Next Distribution
                </Text>
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-base tabular-nums"
                >
                    <Countdown target={effectiveTarget} placeholder="—" />
                </Text>
            </div>
            <div className="h-2 w-full bg-line-base">
                <div
                    className="h-full bg-accent-base"
                    style={{ width: `${progress * 100}%` }}
                />
            </div>
        </>
    );
}
